// File Metadata:
// Filename: NewDepartureFormConfirm.js
// Version: 1.00
// Created: 2025-03-22 00:11:56
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    CardHeader,
    TextField,
    ImageList,
    ImageListItem,
    LinearProgress,
    List,
    ListItem,
    ListItemText
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAddNewDepartureMutation, useGetCrossdocksQuery } from '../../api/crossdockApiSlice'
import uploadToImageKit from '../../../functions/imagekit'

const NewDepartureFormConfirm = ({ prevStep, values }) => {
    const navigate = useNavigate()
    const [addNewDeparture, { isLoading, isSuccess }] = useAddNewDepartureMutation()
    const { data: crossdocks } = useGetCrossdocksQuery()
    
    const [errMsg, setErrMsg] = useState('')
    const [destination, setDestination] = useState('')
    const [crossdockId, setCrossdockId] = useState(null)
    const [files, setFiles] = useState(values.departureImages || [])
    const [progress, setProgress] = useState({})

    useEffect(() => {
        if (crossdocks) {
            const crossdock = crossdocks.entities[
                Object.keys(crossdocks.entities).find(
                    key => crossdocks.entities[key].sscc === values.sscc && !crossdocks.entities[key].departed
                )
            ]
            if (crossdock) {
                setCrossdockId(crossdock._id)
            } else {
                setErrMsg('No matching active crossdock HU found')
            }
        }
    }, [crossdocks, values.sscc])

    useEffect(() => {
        if (isSuccess) {
            navigate('/dash/crossdock')
        }
    }, [isSuccess, navigate])

    const handleUploads = async (data) => {
        if (data.length > 0) {
            let newArr = []
            for (let i = 0; i < data.length; i++) {
                let item = await uploadToImageKit(data[i], (percent) => {
                    setProgress(prev => ({ ...prev, [data[i].name]: percent }));
                });
                newArr.push(item)
            }
            return newArr
        } else {
            return [] // Allow saving without images
        }
    }

    const save = async e => {
        e.preventDefault()
        if (!destination) {
            setErrMsg('Destination is required')
            return
        }

        try {
            const imgData = files.length > 0 ? await handleUploads(files) : []
            await addNewDeparture({
                id: values.pendingHUs[0].crossdockId,
                departureData: {
                    destination,
                    departureTour: values.departureTour,
                    scannedHUs: values.scannedHUs,
                    departureImages: imgData,
                    notes: [`Departed to ${destination} with tour ${values.departureTour}`]
                }
            }).unwrap()
        } catch (err) {
            setErrMsg(err?.data?.message || 'Failed to save')
        }
    }

    return (
        <Card sx={{ width: 345, minWidth: 200, boxShadow: 2, margin: 2 }}>
            <CardHeader title='Confirm Departure Details' />
            <CardContent>
                {errMsg && <Alert severity='error'>{errMsg}</Alert>}
                <List dense sx={{ mb: 2 }}>
                    {values.scannedHUs.map((sscc, index) => (
                        <ListItem key={index}>
                            <ListItemText primary={`HU: ${sscc}`} />
                        </ListItem>
                    ))}
                </List>
                <TextField
                    fullWidth
                    margin="normal"
                    label="Tour Number"
                    value={values.departureTour}
                    disabled
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    required
                />
                {files.length > 0 && (
                    <Box sx={{ margin: 1 }}>
                        <ImageList sx={{ maxWidth: 340 }} cols={3}>
                            {files.map((item, i) => (
                                <ImageListItem cols={1} rows={1} key={i}>
                                    <img src={`${item.img}`} alt={item.name} />
                                    <LinearProgress
                                        variant='determinate'
                                        color='success'
                                        value={progress[item.name] || 0}
                                        sx={{ visibility: progress[item.name] > 0 ? 'visible' : 'hidden' }}
                                    />
                                </ImageListItem>
                            ))}
                        </ImageList>
                    </Box>
                )}
                <ButtonGroup fullWidth variant='contained'>
                    <Button onClick={prevStep}>Back</Button>
                    <Button onClick={save} disabled={isLoading}>Save</Button>
                </ButtonGroup>
            </CardContent>
        </Card>
    )
}

export default NewDepartureFormConfirm
